import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Check, Loader2, MapPin, Power, RefreshCw, Unplug } from 'lucide-react';
import toast from 'react-hot-toast';
import { clearGbpConnectSession } from '../lib/gbpConnectReset';
import { stripGbpOauthSearch } from '../lib/gbpOauthResume';
import { supabase } from '../lib/supabaseClient';
import { invokeErrorMessage } from '../lib/supabaseFunctionError';
import GoogleBusinessConnectButton from './GoogleBusinessConnectButton';
import { GoogleBusinessIcon } from './PlatformIcon';

export interface GbpGrantLocation {
  accountId: string;
  locationId: string;
  title: string;
  placeId: string | null;
  address: string | null;
  enabled: boolean;
  connectionId: string | null;
}

interface GoogleBusinessLocationsManagerProps {
  businessId: string;
  /** Fired after any enable/disable/disconnect so parents can refresh. */
  onChanged?: () => void;
  /** Start Google sign-in immediately when no account is linked yet. */
  autoStart?: boolean;
  /** Reopen the feedback form after Google sign-in. */
  returnContext?: 'feedback';
}

/**
 * Integrations view for the account-level Google sign-in: once connected, the
 * business sees every location Google lets it manage and switches the ones it
 * wants on. Enabled locations appear under "Connected"; the rest under "All".
 */
export default function GoogleBusinessLocationsManager({
  businessId,
  onChanged,
  autoStart = false,
  returnContext,
}: GoogleBusinessLocationsManagerProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [hasGrant, setHasGrant] = useState(false);
  const [locations, setLocations] = useState<GbpGrantLocation[]>([]);
  const [listError, setListError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [disconnecting, setDisconnecting] = useState(false);

  const invoke = useCallback(
    async (body: Record<string, unknown>) => {
      const { data, error } = await supabase.functions.invoke('google-gbp-connect', {
        body: { businessId, ...body },
      });
      if (error || data?.error) {
        throw new Error((await invokeErrorMessage(error, data)) || 'Request failed');
      }
      return data;
    },
    [businessId],
  );

  const load = useCallback(
    async (opts?: { force?: boolean; silent?: boolean }) => {
      if (!opts?.silent) setLoading(true);
      try {
        const data = await invoke({ action: 'locations', force: opts?.force === true });
        setHasGrant(!!data?.hasGrant);
        setLocations((Array.isArray(data?.locations) ? data.locations : []) as GbpGrantLocation[]);
        setListError(data?.listError || null);
      } catch (err) {
        setListError(err instanceof Error ? err.message : 'Failed to load locations');
      } finally {
        setLoading(false);
      }
    },
    [invoke],
  );

  useEffect(() => {
    void load();
  }, [load]);

  const enable = async (loc: GbpGrantLocation) => {
    setBusyId(loc.locationId);
    try {
      await invoke({
        action: 'enable',
        accountId: loc.accountId,
        locationId: loc.locationId,
        locationName: loc.title,
        placeId: loc.placeId,
      });
      toast.success(`${loc.title} is now connected`);
      await load({ silent: true });
      onChanged?.();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to connect location');
    } finally {
      setBusyId(null);
    }
  };

  const disable = async (loc: GbpGrantLocation) => {
    if (!loc.connectionId) return;
    if (
      !window.confirm(
        `Turn off ${loc.title}? Reviews, posts and automation for this location pause until you turn it back on. Nothing is deleted.`,
      )
    ) {
      return;
    }
    setBusyId(loc.locationId);
    try {
      await invoke({ action: 'disable', connectionId: loc.connectionId });
      toast.success(`${loc.title} turned off`);
      await load({ silent: true });
      onChanged?.();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to turn off location');
    } finally {
      setBusyId(null);
    }
  };

  const disconnectAccount = async () => {
    const enabledCount = locations.filter((l) => l.enabled).length;
    if (
      !window.confirm(
        `Disconnect Google from this business?${
          enabledCount ? ` ${enabledCount} location${enabledCount === 1 ? '' : 's'} will be turned off.` : ''
        } You can sign in again any time — history and settings are kept.`,
      )
    ) {
      return;
    }
    setDisconnecting(true);
    try {
      await invoke({ action: 'disconnectAccount' });
      clearGbpConnectSession();
      try {
        sessionStorage.removeItem('feedback_gbp_connect');
      } catch {
        /* ignore */
      }
      const nextSearch = stripGbpOauthSearch(location.search);
      if (nextSearch !== location.search) {
        navigate({ pathname: location.pathname, search: nextSearch }, { replace: true });
      }
      toast.success('Google account disconnected');
      setHasGrant(false);
      setLocations([]);
      onChanged?.();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to disconnect');
    } finally {
      setDisconnecting(false);
    }
  };

  const connected = locations.filter((l) => l.enabled);
  const available = locations.filter((l) => !l.enabled);

  const row = (loc: GbpGrantLocation) => {
    const busy = busyId === loc.locationId;
    return (
      <div
        key={`${loc.accountId}:${loc.locationId}`}
        className="bg-white border border-gray-200 rounded-xl p-3 sm:p-4 flex items-center gap-3"
      >
        <div
          className={`w-10 h-10 rounded-xl border flex items-center justify-center flex-shrink-0 overflow-hidden ${
            loc.enabled ? 'bg-white border-gray-200' : 'bg-gray-50 border-gray-200 grayscale opacity-70'
          }`}
        >
          <GoogleBusinessIcon className="h-10 w-10" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900 truncate">{loc.title}</p>
          {loc.address ? (
            <p className="text-xs text-gray-500 truncate inline-flex items-center gap-1">
              <MapPin className="h-3 w-3 shrink-0" />
              {loc.address}
            </p>
          ) : null}
        </div>
        {loc.enabled ? (
          <button
            type="button"
            disabled={busy || !!busyId}
            onClick={() => void disable(loc)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-[12px] font-semibold text-gray-700 hover:bg-gray-50 hover:text-red-600 hover:border-red-200 disabled:opacity-50 shrink-0"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Power className="h-3.5 w-3.5" />}
            Turn off
          </button>
        ) : (
          <button
            type="button"
            disabled={busy || !!busyId}
            onClick={() => void enable(loc)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#1a73e8] text-white text-[12px] font-semibold hover:bg-[#1765cc] disabled:opacity-50 shrink-0"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
            Connect
          </button>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500 py-6 justify-center">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading your Google locations…
      </div>
    );
  }

  if (!hasGrant) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="p-4 sm:p-5 space-y-4">
          <p className="text-sm text-gray-600">
            Sign in with Google once. You'll see every Business Profile location that account
            manages and choose which ones to run through Nexus.
          </p>
          <GoogleBusinessConnectButton
            size="medium"
            hideStatusIndicator
            autoStart={autoStart}
            returnContext={returnContext}
            onConnectionChange={() => {
              void load({ force: true });
              onChanged?.();
            }}
            onLocationsReady={() => void load({ force: true })}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="p-4 sm:p-5">
          <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
            <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
              <Check className="h-4 w-4 text-green-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-green-800">Google account connected</p>
              <p className="text-xs text-green-600">
                {connected.length === 0
                  ? 'Pick the locations you want to run through Nexus below'
                  : `${connected.length} of ${locations.length} location${locations.length === 1 ? '' : 's'} active`}
              </p>
            </div>
            <button
              type="button"
              onClick={() => void load({ force: true })}
              className="h-8 w-8 rounded-lg text-green-700 hover:bg-green-100 flex items-center justify-center"
              title="Refresh locations from Google"
              aria-label="Refresh locations from Google"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
          </div>
          {listError ? (
            <p className="mt-2 text-[12px] text-amber-700">
              Couldn't refresh the location list from Google ({listError}). Showing what we have.
            </p>
          ) : null}
        </div>
      </div>

      <div className="space-y-2">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide px-0.5">
          Connected location{connected.length === 1 ? '' : 's'}
          {connected.length ? <span className="text-gray-400 font-medium"> · {connected.length}</span> : null}
        </p>
        {connected.length === 0 ? (
          <div className="rounded-xl border border-dashed border-gray-200 px-4 py-6 text-center text-[12px] text-gray-500">
            No locations active yet — connect one below
          </div>
        ) : (
          connected.map(row)
        )}
      </div>

      {available.length > 0 ? (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide px-0.5">
            All locations
            <span className="text-gray-400 font-medium"> · {available.length} more</span>
          </p>
          {available.map(row)}
        </div>
      ) : null}

      <div className="pt-1">
        <button
          type="button"
          onClick={() => void disconnectAccount()}
          disabled={disconnecting}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-red-600 hover:text-red-700 disabled:opacity-50"
        >
          {disconnecting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Unplug className="h-3.5 w-3.5" />}
          Disconnect Google account
        </button>
        <p className="text-[11px] text-gray-400 mt-1">
          Turns every location off and removes Nexus's access to this Google account. History and
          settings are kept for when you reconnect.
        </p>
      </div>
    </div>
  );
}
